import React, {
  useEffect,
  useMemo,
  useState,
  lazy,
  Suspense,
  useRef,
} from 'react';
import {
  ReduxDispatchProps,
  ReduxSelectorProps,
} from '../../../services/constants';
import { AxiosInstance } from 'axios';
import { ComponentConfig } from '@measured/puck';
import { MarginProps } from '../../../puck/reusable-props/margin';
import { PaddingProps } from '../../../puck/reusable-props/padding';
import {
  getBorderProps,
  getcurrentDeviceType,
  getMarginStyles,
  getPaddingStyles,
  storePhoneCode,
} from '../../../utils/helper';
import {
  countryCurrencyList,
  getIsStaging,
  getIsView,
  getIsViewIsEdit,
  PageType,
} from '../../../utils/constants';
import { Switch, Tooltip } from '@mui/material';
import CustomTooltip from '../../../../components/common/tooltip';
import InfoIcon from '@mui/icons-material/Info';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  setCheckoutErrorMessage,
  setPurchaseCompleted,
  setSubmitSuccess,
} from '../../../redux/slices/commonSlice';
import {
  setIsDeclinePopup,
  setDeclineReasonCode,
  setPurchaseToken,
  setCheckoutOrderID,
  setAvailableShippingMethods,
} from '../../../redux/slices/checkoutSlice';
import { EventPublisher } from '../../../utils/publishEvent';
import { v4 as uuidv4 } from 'uuid';
import { handleEventLogger } from '../../../utils/conversionAPI';
import { setCartData, setSalesTax } from '../../../redux/slices/cartSlice';
import { resetState } from '../../../redux/slices/postUpsellStateSlice';
import { IErrorResponse } from '../../../interface/apiInterface';
import { Cart, ShippingMethod } from '../../../interface/checkoutInterface';
import checkoutService from '../../../api/checkoutService';
import '../../text-editor/style.scss';
import { CheckoutCompletePayload } from '../../../interface/apiServiceInterface';
import { CountryWithStateList } from '../../../data/country';
import GoogleButtonLogo from '../../../../assets/icons/GooglePayButtonLogo';
import {
  callVGSDecrypt,
  getProvinceCode,
  splitName,
  updateContactInfo,
  updateShippingAddress,
} from '../applePay-button/paymentHelper';
import { QuillTextEditor } from '../../../puck/reusable-props/quill-editor';
import WidthAndHeight, {
  WidthAndHeightSubProps,
} from '../../../puck/reusable-props/widthAndHeight';
import Border, {
  borderProps,
  IBorderValueProps,
} from '../../../puck/reusable-props/Border';
import BackgroungColorComponent from '../../customComponents/BackgroungColorComponent';
import { postUpsellPage } from '../../../utils/postUpsell';
import PaddingComponent from '../../customComponents/PaddingComponent';
import MarginComponent from '../../customComponents/MarginComponent';
import { useFormContext } from 'react-hook-form';
import { AddressFormType } from '../../../../page-builder/interface/formInterface';
const GooglePayButton = lazy(() => import('@google-pay/button-react'));

export interface GooglePayButtonComponentProps
  extends ReduxSelectorProps,
    ReduxDispatchProps {
  baseService?: AxiosInstance;
}

export type GooglePayButtonProps = {
  isDark: boolean;
  justifyContent: string;
  showOnlyButton: boolean;
  border: IBorderValueProps;
  buttonCustomization: WidthAndHeightSubProps;
  TermsAndConCheck: { isTermsAndConChecked: boolean; errorMsg: string };
  backgroundColor: {
    color: string;
    isInherit: boolean;
  };
  headerText: string;
} & MarginProps &
  PaddingProps;

export const GooglePay = ({
  useAppDispatch,
  useAppSelector,
}: GooglePayButtonComponentProps): ComponentConfig<GooglePayButtonProps> => {
  return {
    label: (
      <CustomTooltip title="GooglePayButton">
        <span>Google Pay</span>
      </CustomTooltip>
    ) as React.ReactNode as string,
    fields: {
      TermsAndConCheck: {
        type: 'custom',
        render: ({ onChange, value }) => {
          return (
            <div className="space-y-4">
              {/* Toggle for isTermsAndConChecked */}
              <div className="flex justify-between items-center">
                <p className="text-sm font-semibold">
                  Require Terms & Conditions
                </p>
                <div className="flex gap-0.5 items-center">
                  <Tooltip
                    title="Toggle to require terms and conditions acceptance"
                    placement="top-start">
                    <InfoIcon fontSize="small" className="h-4 w-4" />
                  </Tooltip>
                  <Switch
                    onChange={e => {
                      onChange({
                        ...value,
                        isTermsAndConChecked: e.target.checked,
                      });
                    }}
                    checked={value?.isTermsAndConChecked}
                    inputProps={{ 'aria-label': 'controlled' }}
                  />
                </div>
              </div>

              {/* Input for errorMsg */}
              {value?.isTermsAndConChecked && (
                <div className="flex flex-col">
                  <label className="text-sm font-semibold mb-1">
                    Error Message for Terms & Conditions
                  </label>
                  <input
                    type="text"
                    value={value?.errorMsg}
                    onChange={e =>
                      onChange({
                        ...value,
                        errorMsg: e.target.value,
                      })
                    }
                    placeholder="Enter error message"
                    className="border border-gray-300 rounded-md p-2 text-sm"
                  />
                </div>
              )}
            </div>
          );
        },
      },
      headerText: {
        ...QuillTextEditor('Header Text').content,
      },
      isDark: {
        type: 'custom',
        render: ({ onChange, value }) => {
          return (
            <div className="flex justify-between items-center">
              <p className="text-sm font-semibold">Show Button as Dark</p>
              <div className="flex gap-0.5 items-center">
                <Tooltip
                  title={'Toggle this switch to show the Button as dark'}
                  placement="top-start">
                  <InfoIcon fontSize="small" className="h-1 w-1" />
                </Tooltip>
                <Switch
                  onChange={e => onChange(e.target.checked)}
                  checked={value}
                  inputProps={{ 'aria-label': 'controlled' }}
                />
              </div>
            </div>
          );
        },
      },
      showOnlyButton: {
        type: 'custom',
        render: ({ onChange, value }) => {
          return (
            <div className="flex justify-between items-center mb-3">
              <p className="text-sm font-semibold">Show only Button</p>
              <div className="flex gap-0.5 items-center">
                <Tooltip
                  title={'Toggle this switch to show only the Button'}
                  placement="top-start">
                  <InfoIcon fontSize="small" className="h-1 w-1" />
                </Tooltip>
                <Switch
                  onChange={e => {
                    onChange(e.target.checked);
                  }}
                  checked={value}
                  inputProps={{ 'aria-label': 'controlled' }}
                />
              </div>
            </div>
          );
        },
      },
      justifyContent: {
        type: 'select',
        label: 'Button Alignment',
        options: [
          { label: 'center', value: 'justify-center' },
          { label: 'left', value: 'justify-start' },
          { label: 'right', value: 'justify-end' },
        ],
      },
      buttonCustomization: {
        type: 'custom',
        render: ({ value, onChange }) => (
          <WidthAndHeight
            value={value}
            onChange={val => {
              onChange({
                ...value,
                ...val,
              });
            }}
            title={'Button Customization'}
          />
        ),
      },
      backgroundColor: {
        type: 'custom',
        render: ({ value, onChange }) => (
          <BackgroungColorComponent
            value={value}
            onChange={onChange}
            title="Background Color"
          />
        ),
      },
      margin: {
        type: 'custom',
        label: 'Margin',
        render: ({ value, onChange }) => {
          return (
            <MarginComponent value={value} onChange={onChange} title="Margin" />
          );
        },
      },
      padding: {
        type: 'custom',
        label: 'Padding',
        render: ({ value, onChange }) => (
          <PaddingComponent value={value} onChange={onChange} title="Padding" />
        ),
      },
      border: {
        type: 'custom',
        render: ({ value, onChange }) => (
          <Border value={value} onChange={onChange} title="Border" />
        ),
      },
    },
    defaultProps: {
      TermsAndConCheck: {
        isTermsAndConChecked: false,
        errorMsg: '*Please accept the terms and conditions to proceed.',
      },
      backgroundColor: {
        color: '#ffffff',
        isInherit: false,
      },
      headerText:
        '<p style="line-height: 1.5;" class="ql-align-center"><span style="font-size: 16px;">Express checkout</span></p>',
      justifyContent: 'justify-center',
      isDark: true,
      margin: {
        desktop: {
          top: '0',
          bottom: '0',
          right: '0',
          left: '0',
        },
        tablet: {
          top: '0',
          bottom: '0',
          right: '0',
          left: '0',
        },
        mobile: {
          top: '0',
          bottom: '0',
          right: '0',
          left: '0',
        },
      },
      padding: {
        deviceType: 'desktop',
        paddingBottom: {
          desktop: '4',
          mobile: '4',
          tablet: '4',
        },
        paddingLeft: {
          desktop: '4',
          mobile: '4',
          tablet: '4',
        },
        paddingRight: {
          desktop: '4',
          mobile: '4',
          tablet: '4',
        },
        paddingTop: {
          desktop: '4',
          mobile: '4',
          tablet: '4',
        },
      },
      ...borderProps,
      buttonCustomization: {
        height: {
          desktop: '47',
          mobile: '47',
          tablet: '47',
        },
        maxWidth: {
          desktop: '160',
          mobile: '160',
          tablet: '160',
        },
        deviceType: 'desktop',
      },
      showOnlyButton: false,
    },
    render: ({
      margin,
      padding,
      border,
      isDark,
      editMode,
      backgroundColor,
      showOnlyButton,
      justifyContent,
      headerText,
      buttonCustomization,
      TermsAndConCheck,
    }) => {
      const dispatch = useAppDispatch();
      const { setValue } = useFormContext<AddressFormType>();
      const [searchParams] = useSearchParams();
      const [canUseGooglePay, setCanUseGooglePay] = useState<boolean>(false);
      const { GooglePay, cartData } = useAppSelector(state => state.cart);
      const { isTermsAccepted } = useAppSelector(state => state.common);
      const { currentPageFunnelId, pageId } = useAppSelector(
        state => state.checkout,
      );
      const { storeData, currentWidth } = useAppSelector(state => state?.store);
      const setCartDataRef = useRef<Cart | null>(null);
      const [loading, setLoading] = useState(false);
      const currentDeviceType = getcurrentDeviceType(currentWidth, editMode);
      const [termsError, setTermsError] = useState<string | null>(null);

      const upsellData = useAppSelector(state => state.postUpSell.upsellData);
      const postUpSellProducts = useAppSelector(
        state => state.postUpSell.postUpsellData,
      );
      const navigate = useNavigate();
      const newSearchParams = new URLSearchParams(searchParams.toString());
      const postUpsellRedirectUrl = `/post-upsell/?${newSearchParams.toString()}`;
      const eventToken = searchParams.get('event-token');
      const igParams = searchParams.get('igParams');
      const publish = EventPublisher.getInstance().publish;
      const smartFunnelId = useAppSelector(
        state => state.postUpSell.smartUpsellFunnelID,
      );
      const NextPageConfig = useAppSelector(
        state => state.checkout.nextpageConfig,
      );
      const isCartApiLoading = useAppSelector(
        state => state.cart.isCartApiLoading,
      );

      const navState = useMemo(() => {
        if (!getIsViewIsEdit() && NextPageConfig?.id) {
          return {
            state: {
              id: NextPageConfig.id,
              pageVisiblity: NextPageConfig.pageVisiblity,
              pageType: NextPageConfig.pageType,
              smartFunnelId:
                NextPageConfig.pageType === PageType?.UPSELL
                  ? smartFunnelId
                  : '',
            },
            replace: true,
          };
        }
        return {};
      }, [
        NextPageConfig?.id,
        NextPageConfig?.pageVisiblity,
        NextPageConfig?.pageType,
        getIsViewIsEdit,
      ]);

      const handleNavigate = () => {
        navigate('/' + '?' + newSearchParams.toString(), navState);
      };

      const completeApi = async (payload: CheckoutCompletePayload) => {
        dispatch(setCheckoutErrorMessage(null));
        dispatch(setIsDeclinePopup(false));
        if (!cartData?.ID || !storeData?.StoreID || !storeData?.ShopID) return;
        if (!getIsViewIsEdit()) {
          publish('PurchaseAttempt', {
            eventId: uuidv4(),
            eventName: 'PurchaseAttempt',
            shopId: storeData?.ShopID,
            cart: cartData,
          });
          publish('AddPaymentInfo', {
            eventId: uuidv4(),
            eventName: 'AddPaymentInfo',
            shopId: storeData?.ShopID,
            cart: cartData,
          });
        }
        dispatch(setSubmitSuccess(true));

        const updatedCart = {
          ...cartData,
          Email: setCartDataRef.current?.Email || cartData.Email,
          ShippingAddress:
            setCartDataRef.current?.ShippingAddress || cartData.ShippingAddress,
          PhoneNumber:
            setCartDataRef.current?.PhoneNumber || cartData.PhoneNumber,
          BillingAddress: payload.BillingAddress,
          TotalAmount:
            setCartDataRef.current?.TotalAmount || cartData.TotalAmount,
          Fees: setCartDataRef.current?.Fees || cartData.Fees,
          SubtotalAmount:
            setCartDataRef.current?.SubtotalAmount || cartData.SubtotalAmount,
          SalesTaxAmount:
            setCartDataRef.current?.SalesTaxAmount || cartData.SalesTaxAmount,
        };

        try {
          const uniqueEventId = uuidv4();
          const response = await checkoutService.checkoutComplete(
            payload,
            uniqueEventId,
          );
          if (!getIsViewIsEdit()) {
            publish('Purchase', {
              eventId: uniqueEventId,
              eventName: 'Purchase',
              shopId: storeData?.ShopID,
              cart: updatedCart,
              orderId: response?.data?.PurchaseToken,
              status: response?.status,
            });
            // this event is moved to backend so commenting it out for now once every this is working fine we can remove this
            // if (CallMetaApi) {
            //   handleCapi({
            //     eventName: 'Purchase',
            //     ShopID: storeData.ShopID,
            //     Cart: cartData,
            //     orderId: response?.data?.PurchaseToken,
            //   });
            // }

            handleEventLogger({
              eventType: 'payment',
              shopifyDetails: {
                shopId: storeData?.ShopID,
              },
              apiDetails: {
                payload,
                response: response.data,
                message: 'Payment Successfull',
              },
              eventToken: eventToken || null,
              cartDetails: cartData,
            });
          }
          dispatch(
            setCartData({
              ...cartData,
              Email: setCartDataRef.current?.Email || cartData.Email,
              ShippingAddress:
                setCartDataRef.current?.ShippingAddress ||
                cartData.ShippingAddress,
              PhoneNumber:
                setCartDataRef.current?.PhoneNumber || cartData.PhoneNumber,
              BillingAddress: payload.BillingAddress,
            }),
          );
          dispatch(setPurchaseToken(response.data?.PurchaseToken));
          dispatch(setCheckoutOrderID(response.data?.OrderID));
          dispatch(resetState());
          dispatch(setPurchaseCompleted(true));
          storePhoneCode('+1');
          let upselldataParsed = '';
          try {
            upselldataParsed = JSON.stringify(upsellData || '{}');
          } catch (error) {
            console.log('Error parsing upsellData:', error);
          }

          if (
            postUpsellPage?.includes(storeData?.ShopID) &&
            postUpSellProducts &&
            postUpSellProducts.length > 0
          ) {
            navigate(postUpsellRedirectUrl);
          } else if (upsellData && upselldataParsed !== '{}') {
            handleNavigate();
          } else {
            dispatch(resetState());
            dispatch(setPurchaseCompleted(true));
            storePhoneCode('+1');
          }
        } catch (e) {
          const error = e as IErrorResponse;
          handleEventLogger({
            eventType: 'payment',
            shopifyDetails: {
              shopId: storeData?.ShopID,
            },
            apiDetails: {
              payload,
              response: error,
              message: 'Payment Failed',
            },
            cartDetails: cartData,
            eventToken: eventToken || null,
          });
          const errorMessage = error?.error?.response?.data?.ProcessMessage
            ? error?.error?.response?.data?.Msg
            : '';
          if (errorMessage) {
            dispatch(setCheckoutErrorMessage(errorMessage));
          } else if (error?.status === 402) {
            dispatch(setIsDeclinePopup(true));
            dispatch(
              setDeclineReasonCode(
                error?.message || 'Invalid card information',
              ),
            );

            dispatch(setSubmitSuccess(false));
          } else if (
            error?.status === 403 &&
            error?.message?.toLowerCase().includes('fraud')
          ) {
            dispatch(setCheckoutErrorMessage(`Order can not be completed`));
          } else {
            dispatch(
              setCheckoutErrorMessage(
                error?.message ||
                  `We encountered an error. Please try again. ${error?.status}`,
              ),
            );
          }
          if (error.status) {
            dispatch(setSubmitSuccess(false));
          }
        } finally {
          dispatch(setSubmitSuccess(false));
        }
      };

      useEffect(() => {
        if (editMode) {
          setCanUseGooglePay(true);
          return;
        }
        const hasBasicSupport = 'fetch' in window && 'Promise' in window;
        setCanUseGooglePay(hasBasicSupport);
      }, [editMode]);

      if (!+GooglePay?.IsGooglePay && !getIsViewIsEdit()) return <></>;
      const buttonstyle: React.CSSProperties =
        currentDeviceType &&
        buttonCustomization?.maxWidth &&
        buttonCustomization?.height &&
        buttonCustomization?.maxWidth?.[currentDeviceType] !== undefined &&
        buttonCustomization?.height?.[currentDeviceType] !== undefined
          ? {
              width: isNaN(+buttonCustomization?.maxWidth?.[currentDeviceType])
                ? buttonCustomization?.maxWidth?.[currentDeviceType]
                : `${+buttonCustomization?.maxWidth?.[currentDeviceType]}px`,
              height: isNaN(+buttonCustomization?.height?.[currentDeviceType])
                ? buttonCustomization?.height?.[currentDeviceType]
                : `${+buttonCustomization?.height?.[currentDeviceType]}px`,
            }
          : {};

      const isDisabled =
        TermsAndConCheck?.isTermsAndConChecked && !isTermsAccepted;

      useEffect(() => {
        if (isTermsAccepted) {
          setTermsError(null); // Clear error automatically on change
        }
      }, [isTermsAccepted]);

      const handlePreviewApplePayClick = () => {
        if (isDisabled) {
          setTermsError(
            TermsAndConCheck?.errorMsg ||
              '*Please accept the terms and conditions to proceed.',
          );
        } else {
          setTermsError(null);
        }
      };
      if (canUseGooglePay === false && !editMode) return <></>;
      const doesHeaderHaveContent =
        headerText && headerText.replace(/<[^>]*>/g, '').trim() !== '';
      return (
        <div
          className={`${justifyContent} flex flex-col space-y-4 px-6 py-8 w-full text-editor-content ${border?.borderStyle?.[currentDeviceType]}`}
          style={{
            ...getMarginStyles(margin, currentDeviceType),
            ...getPaddingStyles(padding, currentDeviceType),
            ...getBorderProps(border, currentDeviceType),
            backgroundColor: backgroundColor?.isInherit
              ? 'inherit'
              : backgroundColor?.color,
          }}>
          {!showOnlyButton && doesHeaderHaveContent && (
            <div dangerouslySetInnerHTML={{ __html: headerText }} />
          )}
          <div className={`${justifyContent} flex`}>
            {(TermsAndConCheck?.isTermsAndConChecked && !isTermsAccepted) ||
            editMode ||
            isCartApiLoading ? (
              <div
                className={`flex items-center justify-center  ${isDark ? (isDisabled ? 'bg-gray-700' : 'bg-black') : isDisabled ? 'bg-gray-400' : 'bg-white'} rounded-lg px-3 border !border-black cursor-pointer`}
                style={{
                  ...buttonstyle,
                }}
                onClick={handlePreviewApplePayClick}>
                <GoogleButtonLogo isDark={isDark} />
              </div>
            ) : (
              <Suspense
                fallback={
                  <div className="w-[240px] h-[47px] bg-gray-200 animate-pulse rounded" />
                }>
                <GooglePayButton
                  environment={getIsView() || getIsStaging() ? 'TEST' : 'TEST'}
                  buttonRadius={8}
                  buttonColor={isDark ? 'black' : 'white'}
                  style={{
                    ...buttonstyle,
                  }}
                  buttonSizeMode="fill"
                  buttonType="short"
                  paymentRequest={{
                    apiVersion: 2,
                    shippingAddressParameters: {
                      allowedCountryCodes: CountryWithStateList.map(
                        item => item.code,
                      ),
                      phoneNumberRequired: true,
                    },
                    shippingAddressRequired: true,
                    emailRequired: true,
                    apiVersionMinor: 0,
                    allowedPaymentMethods: [
                      {
                        type: 'CARD',
                        parameters: {
                          allowedAuthMethods: ['PAN_ONLY', 'CRYPTOGRAM_3DS'],
                          allowedCardNetworks: ['MASTERCARD', 'VISA', 'AMEX'],
                        },
                        tokenizationSpecification: {
                          type: 'PAYMENT_GATEWAY',
                          parameters: {
                            gateway: 'verygoodsecurity',
                            gatewayMerchantId: 'ACkh8iHgFMdUYNgBpKto4YCg',
                          },
                        },
                      },
                    ],
                    merchantInfo: {
                      merchantId: GooglePay?.MerchantID,
                      // merchantName: 'Phoenix Ecommerce Technologies',
                    },
                    callbackIntents: ['SHIPPING_ADDRESS'],
                    transactionInfo: {
                      totalPriceStatus: 'FINAL',
                      totalPriceLabel: 'Total',
                      totalPrice: cartData?.TotalAmount || '0.00',
                      currencyCode: cartData?.currencyCode || 'USD',
                      countryCode:
                        countryCurrencyList.find(
                          item => item.currencyCode === cartData?.currencyCode,
                        )?.countryCode || 'US',
                    },
                  }}
                  onPaymentDataChanged={async intermediatePaymentData => {
                    const countryCode = countryCurrencyList.find(
                      item => item.currencyCode === cartData?.currencyCode,
                    )?.countryCode;
                    try {
                      const { shippingAddress } = intermediatePaymentData;
                      const provinceCode = getProvinceCode(shippingAddress);
                      const Address = {
                        FirstName: '',
                        LastName: '',
                        Address1: '',
                        Address2: '',
                        Organization: '',
                        City: shippingAddress?.locality || '',
                        CountryCode: shippingAddress?.countryCode || '',
                        ProvinceCode:
                          provinceCode ||
                          shippingAddress?.administrativeArea ||
                          '',
                        Zip: shippingAddress?.postalCode || '',
                      };
                      const shippingPayload = {
                        Address: { ...Address, ForApplePaySalesTax: true },
                        ID: cartData?.ID || '',
                        ShopID: storeData.ShopID,
                        StoreID: +(storeData?.StoreID ?? 0),
                        PageID: String(pageId) || '',
                      };
                      const response = await updateShippingAddress(
                        shippingPayload,
                        storeData.ShopID,
                        setCartDataRef,
                      );
                      if (response) {
                        setValue(`Shipping`, Address);
                        dispatch(
                          setAvailableShippingMethods(
                            response?.AvailableShippingMethods as ShippingMethod[],
                          ),
                        );
                        dispatch(
                          setSalesTax({
                            SalesTaxAmount: response?.SalesTaxAmount,
                            TotalAmount: response?.TotalAmount,
                            EstimatedTaxAmount: response?.EstimatedTaxAmount,
                            Fees: response?.Fees,
                            DiscountAmount: response?.DiscountAmount,
                            SubtotalAmount: response?.SubtotalAmount,
                            ShippingRequired: response?.ShippingRequired,
                            ShippingMethodID: response?.ShippingMethodID,
                          }),
                        );
                      }
                      return {
                        newTransactionInfo: {
                          totalPriceStatus: 'FINAL',
                          totalPriceLabel: 'Total',
                          totalPrice:
                            response?.TotalAmount ||
                            cartData?.TotalAmount ||
                            '0.00',
                          currencyCode: cartData?.currencyCode || 'USD',
                          countryCode: countryCode || 'US',
                        },
                      };
                    } catch (error) {
                      return {
                        newTransactionInfo: {
                          totalPriceStatus: 'FINAL',
                          totalPriceLabel: 'Total',
                          totalPrice: cartData?.TotalAmount || '0.00',
                          currencyCode: cartData?.currencyCode || 'USD',
                          countryCode: countryCode || 'US',
                        },
                      };
                    }
                  }}
                  onLoadPaymentData={async ({
                    paymentMethodData: { tokenizationData },
                    email,
                    shippingAddress,
                  }) => {
                    try {
                      const provinceCode = getProvinceCode(shippingAddress);
                      const paymentToken = JSON.parse(tokenizationData.token);
                      setLoading(true);
                      const vgsResponse = await callVGSDecrypt(
                        { google_pay_payload: { token: paymentToken } },
                        GooglePay?.VaultId,
                        GooglePay?.RouteID,
                      );

                      if (!cartData || !storeData?.StoreID || !vgsResponse)
                        return;

                      const contactPayload = {
                        Email: email || '',
                        PhoneNumber: shippingAddress?.phoneNumber || '',
                        ID: cartData?.ID,
                        ShopID: storeData.ShopID,
                        StoreID: +(storeData?.StoreID || 0),
                      };
                      const { firstName, lastName } = splitName(
                        shippingAddress?.name || '',
                      );
                      const addressPayload = {
                        Address1: shippingAddress?.address1 || '',
                        Address2: shippingAddress?.address2 || '',
                        City: shippingAddress?.locality || '',
                        CountryCode: shippingAddress?.countryCode || '',
                        FirstName: firstName || '',
                        LastName: lastName || firstName || '',
                        Organization: '',
                        ProvinceCode:
                          provinceCode ||
                          shippingAddress?.administrativeArea ||
                          '',
                        Zip: shippingAddress?.postalCode || '',
                      };
                      // setcontactinfo
                      await updateContactInfo(contactPayload, storeData.ShopID);

                      const shippingPayload = {
                        Address: addressPayload,
                        ID: cartData?.ID,
                        ShopID: storeData.ShopID,
                        StoreID: +(storeData.StoreID || 0),
                        PageID: String(pageId) || '',
                      };
                      //setshippinginfo
                      await updateShippingAddress(
                        shippingPayload,
                        storeData.ShopID,
                        setCartDataRef,
                      );

                      await completeApi({
                        BillingAddress: addressPayload,
                        ID: cartData?.ID || '',
                        ShopID: storeData.ShopID,
                        StoreID: +(storeData.StoreID || 0),
                        IsTermsChecked: isTermsAccepted,
                        CurrentPageFunnelId: currentPageFunnelId,
                        PageId: pageId || null,
                        CardInfo: {
                          Number:
                            vgsResponse?.google_pay_payload?.token
                              ?.paymentMethodDetails?.pan,
                          Exp: `${vgsResponse?.google_pay_payload?.token?.paymentMethodDetails?.expirationMonth}/${vgsResponse?.google_pay_payload?.token?.paymentMethodDetails?.expirationYear % 100}`,
                          NameOnCard: '',
                          // CVV: '123',
                          IsEncrypted: true,
                          IsGooglePay: true,
                          GooglePay: {
                            cavv: vgsResponse?.google_pay_payload?.token
                              ?.paymentMethodDetails?.cryptogram,
                            eci: vgsResponse?.google_pay_payload?.token
                              ?.paymentMethodDetails.eci,
                          },
                        },
                        igParams: igParams
                          ? JSON.parse(atob(igParams || '{}'))
                          : undefined,
                        //   googlePayData: response?.json,
                      });
                    } catch (error) {
                      console.error('Error in Google Pay:', error);
                    } finally {
                      setLoading(false);
                    }
                  }}
                  // onReadyToPayChange={result => {
                  //   if (!result.isReadyToPay && !editMode) {
                  //     setCanUseGooglePay(false);
                  //   }
                  // }}
                />
              </Suspense>
            )}
            {loading && (
              <div
                className="fixed inset-0 bg-white bg-opacity-70 flex items-center justify-center backdrop-blur-sm"
                style={{ zIndex: 1000 }}>
                <div className="w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" />
              </div>
            )}
          </div>
          {termsError && (
            <p className={`text-red-500 text-sm mt-2 flex ${justifyContent}`}>
              {termsError}
            </p>
          )}

          {!showOnlyButton && (
            <div className="flex items-center w-full">
              <hr className="flex-grow border-t border-gray-300" />
              <span className="mx-2 text-gray-500 text-sm whitespace-nowrap">
                OR
              </span>
              <hr className="flex-grow border-t border-gray-300" />
            </div>
          )}
        </div>
      );
    },
  };
};
